import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StarRating({ value = 0, size = "sm", interactive = false, onChange }) {
    const [hover, setHover] = useState(0);
    const dim = size === "lg" ? "text-xl" : size === "md" ? "text-base" : "text-sm";
    const display = hover || value;
    return (
        <span className={`font-mono ${dim}`}>
            {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= Math.round(display);
                return (
                    <button
                        type="button"
                        key={n}
                        disabled={!interactive}
                        onMouseEnter={() => interactive && setHover(n)}
                        onMouseLeave={() => interactive && setHover(0)}
                        onClick={() => interactive && onChange && onChange(n)}
                        className={`${interactive ? "cursor-pointer" : "cursor-default"} ${filled ? "text-[#E63946]" : "text-zinc-700"} ${interactive ? "hover:scale-110" : ""} transition-transform`}
                        aria-label={interactive ? `Rate ${n}` : undefined}
                        tabIndex={interactive ? 0 : -1}
                    >
                        ★
                    </button>
                );
            })}
        </span>
    );
}

export function ReviewSummary({ summary }) {
    if (!summary || !summary.count) {
        return (
            <span className="text-[10px] font-display tracking-[0.2em] uppercase text-zinc-600">
                No reviews yet
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5">
            <StarRating value={summary.average} size="sm" />
            <span className="text-[10px] font-mono text-zinc-400">
                {summary.average.toFixed(1)} · {summary.count} review{summary.count === 1 ? "" : "s"}
            </span>
        </span>
    );
}

export default function ProductReviews({ productId }) {
    const [open, setOpen] = useState(false);
    const [reviews, setReviews] = useState([]);
    const [summary, setSummary] = useState({ average: 0, count: 0 });
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", body: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/reviews/${productId}`);
            setReviews(res.data?.reviews || []);
            setSummary({ average: res.data?.average || 0, count: res.data?.count || 0 });
        } catch {
            /* ignore */
        }
    }, [productId]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.rating < 1) return toast.error("Pick a star rating");
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/reviews`, {
                product_id: productId,
                ...form,
                email: form.email.trim().toLowerCase(),
                name: form.name.trim(),
                title: form.title.trim(),
                body: form.body.trim(),
            });
            toast.success(res.data?.verified_purchase
                ? "Review submitted · Verified Purchase ✓ Awaiting approval"
                : "Review submitted · Awaiting approval");
            setForm({ name: "", email: "", rating: 5, title: "", body: "" });
            setShowForm(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submit failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="border-t border-white/10 mt-4 pt-4">
            <button
                type="button"
                data-testid={`reviews-toggle-${productId}`}
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between text-[10px] font-display tracking-[0.3em] uppercase text-zinc-400 hover:text-white"
            >
                <span className="flex items-center gap-2">
                    ★ Reviews
                    {summary.count > 0 && (
                        <span className="text-zinc-500">({summary.count})</span>
                    )}
                </span>
                <span>{open ? "−" : "+"}</span>
            </button>

            {open && (
                <div className="mt-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                        <ReviewSummary summary={summary} />
                        <button
                            type="button"
                            data-testid={`reviews-write-${productId}`}
                            onClick={() => setShowForm((v) => !v)}
                            className="text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
                        >
                            {showForm ? "Cancel" : "Write Review"}
                        </button>
                    </div>

                    {showForm && (
                        <form onSubmit={submit} className="space-y-2 bg-black/40 border border-white/10 p-3">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-display tracking-[0.25em] uppercase text-zinc-500">Rating</span>
                                <StarRating
                                    value={form.rating}
                                    size="md"
                                    interactive
                                    onChange={(n) => setForm((p) => ({ ...p, rating: n }))}
                                />
                            </div>
                            <input
                                data-testid={`reviews-form-name-${productId}`}
                                value={form.name}
                                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                                placeholder="Your name"
                                required
                                maxLength={80}
                                className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                            />
                            <input
                                data-testid={`reviews-form-email-${productId}`}
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                                placeholder="email (verifies purchase)"
                                required
                                className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                            />
                            <input
                                data-testid={`reviews-form-title-${productId}`}
                                value={form.title}
                                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                                placeholder="Short headline"
                                required
                                maxLength={120}
                                className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                            />
                            <textarea
                                data-testid={`reviews-form-body-${productId}`}
                                value={form.body}
                                onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
                                placeholder="Your honest take"
                                required
                                minLength={4}
                                maxLength={2000}
                                rows={3}
                                className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white"
                            />
                            <button
                                type="submit"
                                data-testid={`reviews-form-submit-${productId}`}
                                disabled={submitting}
                                className="w-full bg-[#E63946] text-white font-display tracking-[0.25em] uppercase text-[10px] py-2 hover:bg-white hover:text-[#E63946] disabled:opacity-50"
                            >
                                {submitting ? "Submitting…" : "Submit Review"}
                            </button>
                            <p className="text-[10px] text-zinc-600 text-center">
                                All reviews are moderated before going public.
                            </p>
                        </form>
                    )}

                    {reviews.length === 0 ? (
                        <p className="text-[11px] text-zinc-600 italic">Be the first to leave a review.</p>
                    ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {reviews.map((r) => (
                                <div key={r.id} className="border-l-2 border-[#E63946]/40 pl-3 py-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <StarRating value={r.rating} size="sm" />
                                        <span className="font-display text-xs tracking-wider text-white">{r.title}</span>
                                        {r.verified_purchase && (
                                            <span className="text-[8px] font-display tracking-[0.25em] uppercase text-green-400">
                                                ✓ Verified
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{r.body}</p>
                                    <p className="text-[10px] text-zinc-600 mt-1 font-mono">— {r.name}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
